import { useState } from 'react';
import { useLang } from '../hooks/useLang';
import { LegalPageLayout } from '../components/LegalPageLayout';

const content = {
  EN: {
    back: "Back to Home",
    title: "Terms of Use",
    updated: "Last updated: March 2026",
    sections: [
      {
        heading: "1. Acceptance of Terms",
        body: `By accessing or using the website www.koberholding.com (the "Website"), you agree to be bound by these Terms of Use ("Terms"). If you do not agree to these Terms, please discontinue use of the Website immediately.

These Terms apply to all visitors, users, and others who access or use the Website. KÖBER Energy Holding S.R.L. reserves the right to amend these Terms at any time. Continued use of the Website following any amendment constitutes your acceptance of the updated Terms.`,
      },
      {
        heading: "2. About KÖBER Energy Holding S.R.L.",
        body: `KÖBER Energy Holding S.R.L. is a company incorporated under the laws of Romania, with its registered office at 18 Cluceru Udricani Street, 030782, 3rd District, Bucharest, Romania ("KÖBER Energy Holding", "we", "us", "our").

The Website is operated by KÖBER Energy Holding S.R.L. for informational and business development purposes related to our activities in renewable energy development, project financing, battery energy storage systems (BESS), and related technologies including the Holy Grail independent power generation unit.`,
      },
      {
        heading: "3. Permitted Use",
        body: `You may access and use the Website solely for lawful purposes and in accordance with these Terms. You agree not to:

• Use the Website in any way that violates applicable local, national, or international laws or regulations.
• Transmit any unsolicited or unauthorised advertising or promotional material.
• Attempt to gain unauthorised access to any part of the Website or its related systems or networks.
• Engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Website.
• Use any robot, spider, scraper, or other automated means to access the Website without our prior written consent.
• Reproduce, duplicate, copy, sell, resell, or exploit any portion of the Website without our express written permission.`,
      },
      {
        heading: "4. Intellectual Property",
        body: `All content on this Website — including but not limited to text, graphics, logos, images, data, software, documents, and the overall design — is the exclusive property of KÖBER Energy Holding S.R.L. or its licensors and is protected by Romanian and EU intellectual property laws.

The KÖBER Energy Holding name, logo, and all related product and service names, design marks, and slogans are trademarks or registered trademarks of KÖBER Energy Holding S.R.L. Nothing on this Website shall be construed as granting any licence or right to use any trademark without prior written permission.`,
      },
      {
        heading: "5. No Investment Advice",
        body: `The information contained on this Website is provided for general informational and promotional purposes only. It does not constitute, and should not be construed as:

• Financial, investment, legal, tax, or regulatory advice;
• An offer to sell or a solicitation of an offer to buy any securities, financial instruments, or investment products;
• A recommendation to enter into any transaction.

KÖBER Energy Holding S.R.L. is not a regulated investment advisor. Any person considering an investment decision based on information on this Website should seek independent professional advice tailored to their specific circumstances.`,
      },
      {
        heading: "6. Forward-Looking Statements",
        body: `This Website may contain forward-looking statements regarding our business plans, financial projections, project pipelines, and market opportunities. Such statements involve known and unknown risks, uncertainties, and other factors that may cause actual results to differ materially from those expressed or implied.

Forward-looking statements are based on assumptions and estimates that may prove incorrect. We undertake no obligation to update or revise any forward-looking statement except as required by applicable law.`,
      },
      {
        heading: "7. Confidentiality of Investment Materials",
        body: `Certain materials available on or through this Website, including the KÖBER Energy Holding Investment Deck and related documents ("Investment Materials"), are strictly confidential and intended solely for qualified investors, institutional counterparties, and professional advisors.

By downloading or accessing any Investment Materials, you confirm that:

• You are a qualified or sophisticated investor as defined under applicable law;
• You will not reproduce, distribute, or disclose such materials to any third party without our express prior written consent;
• You acknowledge that the Investment Materials do not constitute a prospectus, offering memorandum, or regulated financial promotion.`,
      },
      {
        heading: "8. Third-Party Links",
        body: `The Website may contain links to third-party websites or resources. These links are provided for your convenience only. KÖBER Energy Holding S.R.L. has no control over the content of those sites or resources and accepts no responsibility for them or for any loss or damage that may arise from your use of them.

Inclusion of any link does not imply endorsement by KÖBER Energy Holding S.R.L. of the linked website or its operator.`,
      },
      {
        heading: "9. Disclaimer of Warranties",
        body: `The Website and its content are provided on an "as is" and "as available" basis without warranties of any kind, express or implied, including but not limited to:

• Warranties of merchantability, fitness for a particular purpose, or non-infringement;
• Warranties that the Website will be uninterrupted, error-free, or free of viruses or other harmful components;
• Warranties as to the accuracy, completeness, currency, or reliability of any content.

To the fullest extent permitted by applicable law, KÖBER Energy Holding S.R.L. disclaims all such warranties.`,
      },
      {
        heading: "10. Limitation of Liability",
        body: `To the fullest extent permitted by Romanian and EU law, KÖBER Energy Holding S.R.L. and its directors, officers, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or in connection with your use of the Website, including but not limited to:

• Loss of profits, data, business, or goodwill;
• Any reliance placed on information contained on the Website;
• Unauthorised access to or alteration of your data or transmissions.

Nothing in these Terms shall limit our liability for death or personal injury caused by our negligence, or for fraudulent misrepresentation.`,
      },
      {
        heading: "11. Data Protection",
        body: `Your use of this Website is also governed by our Privacy Policy, which is incorporated into these Terms by reference. Please review our Privacy Policy at www.koberholding.com/privacy to understand our practices regarding personal data collection, use, and protection in compliance with the EU General Data Protection Regulation (GDPR) and applicable Romanian law.`,
      },
      {
        heading: "12. Governing Law and Jurisdiction",
        body: `These Terms and any dispute or claim arising out of or in connection with them shall be governed by and construed in accordance with the laws of Romania, without regard to conflict of law principles.

Any disputes arising from these Terms or your use of the Website shall be subject to the exclusive jurisdiction of the competent courts of Bucharest, Romania, unless mandatory consumer protection laws require otherwise.`,
      },
      {
        heading: "13. Severability",
        body: `If any provision of these Terms is found to be invalid, illegal, or unenforceable by a competent court, that provision shall be deemed modified to the minimum extent necessary to make it enforceable. The remaining provisions shall continue in full force and effect.`,
      },
      {
        heading: "14. Entire Agreement",
        body: `These Terms, together with our Privacy Policy and any other legal notices published on the Website, constitute the entire agreement between you and KÖBER Energy Holding S.R.L. with respect to your use of the Website and supersede all prior agreements, understandings, or representations.`,
      },
      {
        heading: "15. Contact",
        body: `For any questions regarding these Terms of Use, please contact:

KÖBER Energy Holding S.R.L.
18 Cluceru Udricani Street, 030782
3rd District, Bucharest, Romania
Email: office@koberholding.com
Website: www.koberholding.com`,
      },
    ],
  },
  RO: {
    back: "Înapoi la Pagina Principală",
    title: "Termeni și Condiții de Utilizare",
    updated: "Ultima actualizare: Martie 2026",
    sections: [
      {
        heading: "1. Acceptarea Termenilor",
        body: `Prin accesarea sau utilizarea site-ului web www.koberholding.com („Site-ul"), sunteți de acord să respectați prezentele Termeni și Condiții de Utilizare („Termeni"). Dacă nu sunteți de acord cu acești Termeni, vă rugăm să încetați imediat utilizarea Site-ului.

Acești Termeni se aplică tuturor vizitatorilor, utilizatorilor și oricăror alte persoane care accesează sau utilizează Site-ul. KÖBER Energy Holding S.R.L. își rezervă dreptul de a modifica acești Termeni în orice moment. Continuarea utilizării Site-ului după orice modificare constituie acceptarea Termenilor actualizați.`,
      },
      {
        heading: "2. Despre KÖBER Energy Holding S.R.L.",
        body: `KÖBER Energy Holding S.R.L. este o societate constituită în conformitate cu legislația română, cu sediul social la Str. Cluceru Udricani nr. 18, 030782, Sectorul 3, București, România („KÖBER Energy Holding", „noi", „nouă", „noastră").

Site-ul este operat de KÖBER Energy Holding S.R.L. în scopuri informaționale și de dezvoltare a afacerilor, legate de activitățile noastre în domeniul dezvoltării energiei regenerabile, finanțării proiectelor, sistemelor de stocare a energiei în baterii (BESS) și tehnologiilor conexe, inclusiv unitatea Holy Grail de generare independentă a energiei.`,
      },
      {
        heading: "3. Utilizarea Permisă",
        body: `Puteți accesa și utiliza Site-ul exclusiv în scopuri legale și în conformitate cu acești Termeni. Sunteți de acord să nu:

• Utilizați Site-ul în orice mod care încalcă legile sau reglementările locale, naționale sau internaționale aplicabile.
• Transmiteți materiale publicitare sau promoționale nesolicitate sau neautorizate.
• Încercați să obțineți acces neautorizat la orice parte a Site-ului sau la sistemele sau rețelele aferente.
• Vă implicați în orice conduită care restricționează sau inhibă utilizarea sau bucuria Site-ului de către alții.
• Utilizați roboți, crawlere sau alte mijloace automate pentru a accesa Site-ul fără consimțământul nostru prealabil scris.
• Reproduceți, duplicați, copiați, vindeți sau exploatați orice parte a Site-ului fără permisiunea noastră scrisă expresă.`,
      },
      {
        heading: "4. Proprietate Intelectuală",
        body: `Tot conținutul acestui Site — incluzând, fără a se limita la, texte, grafice, logo-uri, imagini, date, software, documente și design-ul general — este proprietatea exclusivă a KÖBER Energy Holding S.R.L. sau a licențiatorilor săi și este protejat de legile privind proprietatea intelectuală din România și UE.

Denumirea KÖBER Energy Holding, logo-ul și toate denumirile de produse și servicii conexe, mărcile de design și sloganurile sunt mărci comerciale sau mărci înregistrate ale KÖBER Energy Holding S.R.L. Nimic de pe acest Site nu va fi interpretat ca acordând o licență sau dreptul de a utiliza orice marcă comercială fără permisiunea noastră prealabilă scrisă.`,
      },
      {
        heading: "5. Fără Consiliere de Investiții",
        body: `Informațiile conținute pe acest Site sunt furnizate exclusiv în scop informativ general și promoțional. Acestea nu constituie și nu trebuie interpretate ca:

• Consiliere financiară, de investiții, juridică, fiscală sau de reglementare;
• O ofertă de vânzare sau o solicitare de a cumpăra orice valori mobiliare, instrumente financiare sau produse de investiții;
• O recomandare de a încheia orice tranzacție.

KÖBER Energy Holding S.R.L. nu este un consilier de investiții reglementat. Orice persoană care ia în considerare o decizie de investiție bazată pe informații de pe acest Site ar trebui să solicite consultanță profesională independentă adaptată situației sale specifice.`,
      },
      {
        heading: "6. Declarații Prospective",
        body: `Acest Site poate conține declarații prospective privind planurile noastre de afaceri, proiecțiile financiare, portofoliul de proiecte și oportunitățile de piață. Astfel de declarații implică riscuri cunoscute și necunoscute, incertitudini și alți factori care pot determina ca rezultatele reale să difere semnificativ de cele exprimate sau implicate.

Declarațiile prospective se bazează pe ipoteze și estimări care se pot dovedi incorecte. Nu ne asumăm nicio obligație de a actualiza sau revizui declarațiile prospective, cu excepția cazului în care legislația aplicabilă impune acest lucru.`,
      },
      {
        heading: "7. Confidențialitatea Materialelor de Investiții",
        body: `Anumite materiale disponibile pe sau prin intermediul acestui Site, inclusiv Prospectul de Investiții KÖBER Energy Holding și documentele aferente („Materialele de Investiții"), sunt strict confidențiale și destinate exclusiv investitorilor calificați, contrapărților instituționale și consilierilor profesioniști.

Prin descărcarea sau accesarea oricăror Materiale de Investiții, confirmați că:

• Sunteți un investitor calificat sau sofisticat în conformitate cu legislația aplicabilă;
• Nu veți reproduce, distribui sau dezvălui aceste materiale niciunei terțe părți fără consimțământul nostru prealabil scris explicit;
• Înțelegeți că Materialele de Investiții nu constituie un prospect, memorandum de ofertă sau promovare financiară reglementată.`,
      },
      {
        heading: "8. Linkuri către Terți",
        body: `Site-ul poate conține linkuri către site-uri web sau resurse ale unor terți. Aceste linkuri sunt furnizate exclusiv pentru comoditatea dvs. KÖBER Energy Holding S.R.L. nu exercită control asupra conținutului acestor site-uri sau resurse și nu acceptă nicio responsabilitate pentru acestea sau pentru orice pierdere sau daună care poate rezulta din utilizarea lor.

Includerea oricărui link nu implică aprobarea de către KÖBER Energy Holding S.R.L. a site-ului web sau a operatorului său.`,
      },
      {
        heading: "9. Excluderea Garanțiilor",
        body: `Site-ul și conținutul său sunt furnizate „ca atare" și „în funcție de disponibilitate", fără garanții de niciun fel, exprese sau implicite, incluzând, fără a se limita la:

• Garanții de comercializare, adecvare pentru un anumit scop sau neîncălcare;
• Garanții că Site-ul va fi neîntrerupt, fără erori sau lipsit de viruși sau alte componente dăunătoare;
• Garanții privind acuratețea, caracterul complet, actualitatea sau fiabilitatea oricărui conținut.

În măsura maximă permisă de legislația aplicabilă, KÖBER Energy Holding S.R.L. declină toate astfel de garanții.`,
      },
      {
        heading: "10. Limitarea Răspunderii",
        body: `În măsura maximă permisă de legislația română și europeană, KÖBER Energy Holding S.R.L. și directorii, ofițerii, angajații și agenții săi nu vor fi răspunzători pentru niciun prejudiciu indirect, incidental, special, consecutiv sau punitiv rezultat din sau în legătură cu utilizarea Site-ului, incluzând, fără a se limita la:

• Pierderea de profituri, date, afaceri sau fond comercial;
• Orice încredere acordată informațiilor conținute pe Site;
• Accesul neautorizat la sau modificarea datelor sau transmisiilor dvs.

Nimic din acești Termeni nu va limita răspunderea noastră pentru deces sau vătămare corporală cauzată din neglijența noastră sau pentru declarații frauduloase.`,
      },
      {
        heading: "11. Protecția Datelor",
        body: `Utilizarea acestui Site este guvernată și de Politica noastră de Confidențialitate, care este incorporată în acești Termeni prin referință. Vă rugăm să consultați Politica noastră de Confidențialitate la adresa www.koberholding.com/privacy pentru a înțelege practicile noastre privind colectarea, utilizarea și protecția datelor cu caracter personal, în conformitate cu Regulamentul General privind Protecția Datelor (RGPD) și legislația română aplicabilă.`,
      },
      {
        heading: "12. Legea Aplicabilă și Jurisdicția",
        body: `Acești Termeni și orice dispută sau pretenție rezultată din sau în legătură cu aceștia vor fi guvernate și interpretate în conformitate cu legile României, fără a lua în considerare principiile conflictului de legi.

Orice dispute rezultate din acești Termeni sau din utilizarea Site-ului vor fi supuse jurisdicției exclusive a instanțelor competente din București, România, cu excepția cazului în care legile obligatorii de protecție a consumatorilor prevăd altfel.`,
      },
      {
        heading: "13. Separabilitate",
        body: `Dacă orice prevedere a acestor Termeni este considerată invalidă, ilegală sau neexecutabilă de către o instanță competentă, acea prevedere va fi considerată modificată în măsura minimă necesară pentru a o face executabilă. Prevederile rămase vor continua să fie pe deplin valabile și în vigoare.`,
      },
      {
        heading: "14. Acordul Integral",
        body: `Acești Termeni, împreună cu Politica noastră de Confidențialitate și orice alte notificări legale publicate pe Site, constituie acordul integral între dvs. și KÖBER Energy Holding S.R.L. cu privire la utilizarea Site-ului și înlocuiesc toate acordurile, înțelegerile sau reprezentările anterioare.`,
      },
      {
        heading: "15. Contact",
        body: `Pentru orice întrebări privind acești Termeni și Condiții de Utilizare, vă rugăm să contactați:

KÖBER Energy Holding S.R.L.
Str. Cluceru Udricani nr. 18, 030782
Sectorul 3, București, România
E-mail: office@koberholding.com
Website: www.koberholding.com`,
      },
    ],
  },
};

export default function TermsOfUse() {
  const [lang, setLang] = useLang();
  return <LegalPageLayout c={content[lang]} lang={lang} setLang={setLang} testIdPrefix="terms" />;
}
